import { Component, Input, OnInit } from '@angular/core';
import {DataService} from '../../../../../Services/data.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { LocationModel } from '../../../../../DataModels/location-model';
import { NotificationService } from '../../../../../Services/notification.service';
import { LocationGroupModel } from '../../../../../DataModels/location-group-model';
import { ConfirmDialogService } from '../../../../../Services/confirm-dialog.service';
import { CompanyModel } from '../../../../../DataModels/company-model';
import { EntityModel } from '../../../../../DataModels/entity-model';
import { ResponseModel } from '../../../../../DataModels/response-model';
import { BsToastService } from '../../../../../Services/bs-toast-service';

@Component({
  selector: 'app-location-detail',
  templateUrl: './location-detail.component.html',
  styleUrls: ['./location-detail.component.css'],
  standalone: false
})
export class LocationDetailComponent implements OnInit {
  @Input() locationID: string;
  location: LocationModel;
  locationForm: UntypedFormGroup;
  locationCount = 0;

  locationGroup$: LocationGroupModel[];
  company$: CompanyModel[];
  entity$: EntityModel[];

  submitted = false;
  isSaveNew = false;
  originalSnapshot: any = {};

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService
  ) {
    this.createForm();
    this.locationID = this.route.snapshot.params.id;
  }

  get f() {
    return this.locationForm.controls;
  }

  createForm() {
    this.locationForm = this.formBuilder.group({
      name: new UntypedFormControl('', [Validators.required]),
      address_1: new UntypedFormControl(''),
      address_2: new UntypedFormControl(''),
      city: new UntypedFormControl(''),
      state: new UntypedFormControl(''),
      zip: new UntypedFormControl(''),
      group_id: new UntypedFormControl('', [Validators.required]),
      active: new UntypedFormControl('true'),
      company_id: new UntypedFormControl('', [Validators.required]),
      // entity_id: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit() {
    this.loadLookups();
    if (this.locationID) {
      this.LoadData();
    } else {
      Promise.resolve().then(() => {
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
    }
  }

  private LoadData(): void {
    this.table.getData('location', this.locationID).subscribe(
      (result) => {
        this.location = result;
      },
      (error: HttpErrorResponse) => {
        // this.notification.error($localize`:Error loading Location @@errorloadingLocation:Error loading Location`);
        console.log(error + ' - ' + $localize`:Error loading Location @@errorloadingLocation:Error loading Location`);
      },
      () => {
        this.locationForm.patchValue(this.location);
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      }
    );
  }

  private loadLookups(): void {
    this.table.getApiListData('location_group?pageNumber=0&pageSize=999999').subscribe((result: ResponseModel) => {
      const data = result.Data as LocationGroupModel[];
      this.locationGroup$ = data;
    });

    this.table.getApiListData('company?pageNumber=0&pageSize=999999').subscribe((result: ResponseModel) => {
      const data = result.Data as CompanyModel[];
      this.company$ = data;
    });

    this.table.getData('entity?pageNumber=0&pageSize=999999').subscribe((result: any) => {
      this.entity$ = result.Data as EntityModel[];
    });
  }

  // saveClose(): void {
  //   if (this.onSave()) {
  //     this.onClose();
  //   }
  // }
  saveClose() {
    if (this.onSave(true)) {
    }
  }

  saveNew(): void {
    // Defer clearing until async save completes
    this.isSaveNew = true;
    this.onSave(false);
  }

  deleteItem(): void {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete location? @@areYouSureYouWantToDeleteLocation:Are you sure you want to delete location?`,
      this.location.name,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table.deleteDataById('location', this.locationID).subscribe(
          (result) => {
            retMsg = result;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
            this.onClose();
          },
          (error: HttpErrorResponse) => {
            // this.notification.error($localize`:Error @@error:Error`);
            console.log(error);
          },
          () => {
            this.onClose();
          }
        );
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled @@deleteCanceled:Delete Canceled`);
      }
    );
  }

  resetForm(): void {
    this.resetToDefaults();
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  onClose(): void {
    this.router.navigate(['admin/location-list']).then();
  }

  onSave(saveClose: boolean): boolean {
    this.submitted = true;
    if (this.locationForm.invalid) {
      this.notification.showWarningToast('Form Errors!');
      return false;
    }
    this.location = Object.assign({}, this.locationForm.value);
    if (this.locationID) {
      if (this.locationForm.invalid === false) {
        this.table.patch('location', this.locationID, this.location).then(
          () => {
            try {
              this.originalSnapshot = this.buildSnapshot();
            } catch {
            }
            if (saveClose) {
              this.onClose();
            } else if (this.isSaveNew) {
              this.isSaveNew = false;
              this.resetToDefaults();
            }
          },
          (error: HttpErrorResponse) => {
            console.log(error);
          }
        ).finally(() => {
          this.submitted = false;
        });
      }
    } else {
      this.table.postWithPromise('location', this.location).then((res) => {
        // stops double posting when clicking Save
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.locationID = apiReturn?.id;
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
        if (saveClose) {
          this.onClose();
        } else if (this.isSaveNew) {
          this.isSaveNew = false;
          this.resetToDefaults();
        }
      }).catch((error: HttpErrorResponse) => {
        console.log(error);
      }).finally(() => {
        this.submitted = false;
      });
      return true;
    }
    this.submitted = false;
    return true;
  }

  private resetToDefaults(): void {
    // Reset the form to known default values and clear component state for a fresh entry
    this.locationForm.reset({
      name: '',
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      zip: '',
      group_id: '',
      active: true,
      company_id: '',
    });
    this.location = {
      name: '',
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      zip: '',
      group_id: '',
      active: true,
      company_id: ''
    } as unknown as LocationModel;
    this.locationID = null;
    this.submitted = false;
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  Reset(): void {
    this.resetToDefaults();
  }

  buildSnapshot(): any {
    try {
      return this.locationForm.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalSnapshot || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  checkForChanges(): boolean {
    return this.hasChanges();
  }
}
